import React, { useState, useEffect, useRef } from 'react';
import { ArrowUpCircle, ClipboardList, Plus, Edit, Trash2, Monitor, Eye, EyeOff, Search, Zap, Layers, Filter, ChevronLeft, ChevronRight, X, Clock, Save, Star, StarOff, ChevronsUp, ChevronUp, ChevronDown, DownloadCloud, UploadCloud, CheckCircle, FileText } from 'lucide-react'; 
import { adminStaffService } from '../../services/adminStaffService';
import catalogService from '../../services/catalogService'; 
import { PRODUCT_STATUS, PRODUCT_STATUS_CONFIG } from '../../utils/constants';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { generateSlug } from '../../utils/helpers';


const getSessionState = (key, defaultValue) => {
    const saved = sessionStorage.getItem(key);
    return saved !== null ? JSON.parse(saved) : defaultValue;
};

const AdminItemList = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const [searchTerm, setSearchTerm] = useState(() => getSessionState('admin_searchTerm', ''));
    const [selectedCategory, setSelectedCategory] = useState(() => getSessionState('admin_selectedCategory', ''));
    const [selectedPurpose, setSelectedPurpose] = useState(() => getSessionState('admin_selectedPurpose', ''));
    const [showOnlyFlashSale, setShowOnlyFlashSale] = useState(() => getSessionState('admin_showOnlyFlashSale', false));
    const [showOnlyBestSeller, setShowOnlyBestSeller] = useState(() => getSessionState('admin_showOnlyBestSeller', false));
    const [showOnlyMainPage, setShowOnlyMainPage] = useState(() => getSessionState('admin_showOnlyMainPage', false));
    const [selectedStatus, setSelectedStatus] = useState(() => getSessionState('admin_selectedStatus', ''));
    const [currentPage, setCurrentPage] = useState(() => getSessionState('admin_currentPage', 1));
    
    const itemsPerPage = 10; 

    const [categories, setCategories] = useState([]);
    const [purposes, setPurposes] = useState([]);
    const [brands, setBrands] = useState([]);
    const [pcComponents, setPcComponents] = useState([]);

    const [editingFlashSaleItem, setEditingFlashSaleItem] = useState(null);
    const [flashSaleForm, setFlashSaleForm] = useState({ price: '', quantity: '' });
    const [updatingFlashSale, setUpdatingFlashSale] = useState(false);

    const [systemFlashSaleTime, setSystemFlashSaleTime] = useState({ start: '', end: '' });
    const [savingTime, setSavingTime] = useState(false);

    const [rentalNote, setRentalNote] = useState("");
    const [savingNote, setSavingNote] = useState(false);

    const [originalConfigs, setOriginalConfigs] = useState({});

    const fileInputRef = useRef(null);
    const [importState, setImportState] = useState({ showModal: false, validRows: [], invalidRows: [], isImporting: false, progress: 0 });

    const maxPriority = items.length > 0 ? Math.max(...items.map(i => i.priority || 0)) : 0;

    const [deleteConfirmTarget, setDeleteConfirmTarget] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [itemsData, catData, purposeData, configRes, brandData, pcCompData] = await Promise.all([
                adminStaffService.getAllItems().catch(() => []),
                adminStaffService.getCategories().catch(() => []),
                adminStaffService.getPurposes().catch(() => []),
                catalogService.getSystemConfig().catch(() => ({})),
                adminStaffService.getBrands().catch(() => []),
                adminStaffService.getAllPcComponents().catch(() => [])
            ]);
            
            const sortedItems = (itemsData || []).sort((a, b) => {
                const prioA = a.priority || 0;
                const prioB = b.priority || 0;
                if (prioA !== prioB) return prioB - prioA; 

                const timeA = new Date(a.itemUpdatedTime || a.itemCreatedTime || 0).getTime();
                const timeB = new Date(b.itemUpdatedTime || b.itemCreatedTime || 0).getTime();
                if (timeA !== timeB && (timeA > 0 || timeB > 0)) return timeB - timeA; 
                return b.itemId - a.itemId;
            });
            
            setItems(sortedItems);
            
            const safeArray = (data) => Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
            const sortedCategories = safeArray(catData).sort((a, b) => {
                const nameA = a.deviceCategoryName || '';
                const nameB = b.deviceCategoryName || '';
                return nameA.localeCompare(nameB, 'vi'); 
            });

            // 2. Sắp xếp Nhu cầu/Danh mục theo thứ tự (order)
            const sortedPurposes = safeArray(purposeData).sort((a, b) => {
                const diff = (a.purposeOrder || 0) - (b.purposeOrder || 0);
                // Nếu cả 2 đều bằng 0 (chưa xếp hạng), fallback về purposeId để thứ tự luôn ổn định
                return diff !== 0 ? diff : (a.purposeId || 0) - (b.purposeId || 0);
            });

            setCategories(sortedCategories); 
            setPurposes(sortedPurposes);
            setBrands(safeArray(brandData));
            setPcComponents(safeArray(pcCompData));

            const conf = configRes?.data || configRes;
            setOriginalConfigs(conf);
            if(conf.flash_sale_start) setSystemFlashSaleTime(prev => ({...prev, start: conf.flash_sale_start}));
            if(conf.flash_sale_end) setSystemFlashSaleTime(prev => ({...prev, end: conf.flash_sale_end}));
            
            // THÊM DÒNG NÀY ĐỂ LẤY GHI CHÚ TỪ SERVER
            if(conf.rental_note) setRentalNote(conf.rental_note);

        } catch (error) { 
            console.error("Lỗi:", error); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { fetchData(); }, []);

    useEffect(() => {
        sessionStorage.setItem('admin_searchTerm', JSON.stringify(searchTerm));
        sessionStorage.setItem('admin_selectedCategory', JSON.stringify(selectedCategory));
        sessionStorage.setItem('admin_selectedPurpose', JSON.stringify(selectedPurpose));
        sessionStorage.setItem('admin_showOnlyFlashSale', JSON.stringify(showOnlyFlashSale));
        sessionStorage.setItem('admin_showOnlyMainPage', JSON.stringify(showOnlyMainPage));
        sessionStorage.setItem('admin_showOnlyBestSeller', JSON.stringify(showOnlyBestSeller));
        sessionStorage.setItem('admin_selectedStatus', JSON.stringify(selectedStatus));
        sessionStorage.setItem('admin_currentPage', JSON.stringify(currentPage));
    }, [searchTerm, selectedCategory, selectedPurpose, showOnlyFlashSale, showOnlyMainPage, showOnlyBestSeller, selectedStatus, currentPage]);

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) { isFirstRender.current = false; return; }
        setCurrentPage(1);
    // CẬP NHẬT MẢNG Ở ĐÂY:
    }, [searchTerm, selectedCategory, selectedPurpose, showOnlyFlashSale, showOnlyMainPage, showOnlyBestSeller, selectedStatus]);

    const updateItemInState = (itemId, updatedFields) => {
        setItems(prevItems => {
            const newItems = prevItems.map(item => 
                item.itemId === itemId ? { ...item, ...updatedFields } : item
            );
            newItems.sort((a, b) => {
                const prioA = a.priority || 0;
                const prioB = b.priority || 0;
                if (prioA !== prioB) return prioB - prioA; 
                const timeA = new Date(a.itemUpdatedTime || a.itemCreatedTime || 0).getTime();
                const timeB = new Date(b.itemUpdatedTime || b.itemCreatedTime || 0).getTime();
                if (timeA !== timeB && (timeA > 0 || timeB > 0)) return timeB - timeA; 
                return b.itemId - a.itemId;
            });
            return newItems;
        });
    };

    const handleDownloadTemplate = () => {
        const headers = [
            "Tên sản phẩm *", "Mã sản phẩm *", "Loại sản phẩm *", "Tên hãng *", 
            "Trạng thái bán (0-5) *", "Thời gian bảo hành", "Giá gốc", "Giá khuyến mãi", 
            "Giá Flash Sale", "SL Flash Sale", "SL Kho *", "Hiện trang chủ (x: có / o: không) *", 
            "Bán chạy (x: có / o: không) *", "PC Builder Component", "Cấu hình chung"
        ];
        
        const sampleRow = [
            "Laptop Gaming Asus ROG", "ASUS-ROG-001", "Laptop", "Asus", 
            2, "24 Tháng", 25000000, 23000000, 
            "", "", 10, "x", 
            "o", "", "- CPU: Intel i7\n- RAM: 16GB"
        ];

        const ws = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
        
        ws['!cols'] = [
            { wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, 
            { wch: 22 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, 
            { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 22 }, 
            { wch: 18 }, { wch: 25 }, { wch: 40 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        XLSX.writeFile(wb, "Template_Import_SanPham.xlsx");
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, { type: 'binary' });
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            const data = XLSX.utils.sheet_to_json(ws, { header: 1 }); 

            if (data.length <= 1) {
                toast.error("File Excel không có dữ liệu!");
                return;
            }

            const validRows = [];
            const invalidRows = [];

            for (let i = 1; i < data.length; i++) {
                const row = data[i];
                if (!row || row.length === 0 || !row.some(cell => cell)) continue; 

                const name = row[0];
                const code = row[1];
                const catName = row[2];
                const brandName = row[3];
                const status = row[4];
                const warranty = row[5] || '';
                const releasePrice = row[6];
                const salePrice = row[7] || null;
                const fsPrice = row[8] || null;
                const fsQty = row[9] || null;
                const qty = row[10];
                const isMain = row[11];
                const isBest = row[12];
                const pcCompName = row[13];
                const generalConfig = row[14] || '';

                let errors = [];

                if (!name) errors.push("Thiếu Tên SP");
                if (!code) errors.push("Thiếu Mã SP");

                let catId = null;
                if (catName) {
                    const foundCat = categories.find(c => c.deviceCategoryName.toLowerCase() === String(catName).toLowerCase().trim());
                    if (foundCat) catId = foundCat.deviceCategoryId;
                    else errors.push(`Không tìm thấy Loại SP: ${catName}`);
                } else errors.push("Thiếu Loại SP");

                let brandId = null;
                if (brandName) {
                    const foundBrand = brands.find(b => b.brandName.toLowerCase() === String(brandName).toLowerCase().trim());
                    if (foundBrand) brandId = foundBrand.brandId;
                    else errors.push(`Không tìm thấy Hãng: ${brandName}`);
                } else errors.push("Thiếu Hãng");

                if (status === undefined || status === null || !Object.values(PRODUCT_STATUS).includes(Number(status))) {
                    errors.push("Trạng thái bán không hợp lệ (phải từ 0 đến 5)");
                }

                if (releasePrice !== undefined && releasePrice !== null && releasePrice !== '' && isNaN(Number(releasePrice))) {
                    errors.push("Giá gốc phải là một số hợp lệ");
                }
                if (qty === undefined || qty === null || isNaN(Number(qty))) {
                    errors.push("Giới hạn số lượng đặt hàng không hợp lệ");
                }

                const checkXO = (val) => {
                    const cleanVal = String(val || '').toLowerCase().trim();
                    if (cleanVal === 'x') return true;
                    if (cleanVal === 'o' || cleanVal === '0') return false;
                    return null;
                };

                const isMainBool = checkXO(isMain);
                if (isMainBool === null) errors.push("Cột Hiện trang chủ phải điền x hoặc o");

                const isBestBool = checkXO(isBest);
                if (isBestBool === null) errors.push("Cột Bán chạy phải điền x hoặc o");

                let pcCompId = null;
                if (pcCompName) {
                    const foundPc = pcComponents.find(p => p.componentName.toLowerCase() === String(pcCompName).toLowerCase().trim() || p.componentCode.toLowerCase() === String(pcCompName).toLowerCase().trim());
                    if (foundPc) pcCompId = foundPc.componentId;
                    else errors.push(`Không tìm thấy PC Component: ${pcCompName}`);
                }

                if (errors.length > 0) {
                    invalidRows.push({ row: i + 1, name: name || 'Sản phẩm không tên', errors });
                } else {
                    validRows.push({
                        itemName: String(name).trim(),
                        itemCode: String(code).trim(),
                        categoryId: catId,
                        brandId: brandId,
                        status: Number(status),
                        warrantyTime: String(warranty).trim(),
                        releasePrice: (releasePrice !== undefined && releasePrice !== null && releasePrice !== '') ? Number(releasePrice) : null,
                        salePrice: salePrice ? Number(salePrice) : null,
                        flashSalePrice: fsPrice ? Number(fsPrice) : null,
                        flashSaleQuantity: fsQty ? Number(fsQty) : null,
                        quantity: Number(qty),
                        isShownInMainPage: isMainBool,
                        isBestSeller: isBestBool,
                        pcComponentId: pcCompId,
                        generalConfig: String(generalConfig)
                        .replace(/\r\n|\n/g, '<br/>') 
                        .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;') 
                        .replace(/ {2,}/g, ' ') 
                    });
                }
            }

            setImportState({ validRows, invalidRows, showModal: true, isImporting: false, progress: 0 });
        };
        reader.readAsBinaryString(file);
        e.target.value = null; 
    };

    const executeImport = async () => {
        setImportState(prev => ({...prev, isImporting: true, progress: 0}));
        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < importState.validRows.length; i++) {
            const row = importState.validRows[i];
            const payload = {
                itemInfo: {
                    itemName: row.itemName,
                    itemCode: row.itemCode,
                    itemReleasePrice: row.releasePrice,
                    itemSalePrice: row.salePrice,
                    itemFlashSalePrice: row.flashSalePrice,
                    itemFlashSaleQuantity: row.flashSaleQuantity,
                    itemQuantity: row.quantity,
                    itemStatus: row.status,
                    categoryId: row.categoryId,
                    brandId: row.brandId,
                    isShownInMainPage: row.isShownInMainPage,
                    isBestSeller: row.isBestSeller,
                    description: '', 
                    generalConfig: row.generalConfig,
                    warrantyTime: row.warrantyTime,
                    priority: 0
                },
                purposes: [], sales: [], itemSpecs: [],
                pcComponentId: row.pcComponentId,
                compatibilityIds: []
            };

            const formData = new FormData();
            formData.append("productData", JSON.stringify(payload));

            try {
                await adminStaffService.createNewItem(formData);
                successCount++;
            } catch(e) { 
                failCount++; 
            }

            setImportState(prev => ({...prev, progress: Math.round(((i + 1) / importState.validRows.length) * 100)}));
        }

        toast.success(`Import hoàn tất: ${successCount} thành công, ${failCount} thất bại.`);
        setImportState({ validRows: [], invalidRows: [], showModal: false, isImporting: false, progress: 0 });
        fetchData(); 
    };

    const handleDelete = (item) => {
        setDeleteConfirmTarget(item);
    };

    const executeDelete = async (id, statusId) => {
        try { 
            await adminStaffService.updateItemStatus(id, statusId); 
            
            toast.success(statusId === 5 ? "Đã chuyển sang Ngừng kinh doanh!" : "Đã ẩn hoàn toàn sản phẩm!");
            updateItemInState(id, { itemStatus: statusId, isShownInMainPage: false });
            setDeleteConfirmTarget(null);
        } catch (error) { 
            toast.error("Lỗi khi vô hiệu hóa!"); 
        }
    };

    const handleToggleMainPage = async (item) => {
        try { 
            await adminStaffService.toggleMainPage(item.itemId); 
            updateItemInState(item.itemId, { isShownInMainPage: !item.isShownInMainPage });
        } catch (error) { toast.error("Lỗi chuyển trạng thái!"); }
    };

    const handleToggleBestSeller = async (item) => {
        try {
            const formData = new FormData();
            const payload = {
                itemInfo: {
                    itemName: item.itemName,
                    itemCode: item.itemCode,
                    itemReleasePrice: item.itemReleasePrice,
                    itemSalePrice: item.itemSalePrice,
                    itemFlashSalePrice: item.itemFlashSalePrice,
                    itemFlashSaleQuantity: item.itemFlashSaleQuantity,
                    itemQuantity: item.itemQuantity,
                    itemStatus: item.itemStatus,
                    categoryId: item.categoryId,
                    brandId: item.brandId,
                    isShownInMainPage: item.isShownInMainPage,
                    isBestSeller: !item.isBestSeller, 
                    description: item.description,
                    warrantyTime: item.warrantyTime,
                    priority: item.priority || 0,
                    generalConfig: item.generalConfig,
                    isForRent: item.isForRent,
                    rentalPrices: item.rentalPrices
                },
                purposes: item.purposeIds || [],
                sales: item.saleIds || [],
                itemSpecs: item.itemSpecs ? item.itemSpecs.map(s => ({ specId: s.specId, specName: s.specName, specValue: s.specValue })) : [],
                retainedImageIds: item.images ? item.images.map(img => img.itemImageId) : [],
                existingMainImageId: item.images?.find(img => img.isMain)?.itemImageId || null,
                pcComponentId: item.pcComponentId || null,
                compatibilityIds: item.compatibilityIds || []
            };

            formData.append("productData", JSON.stringify(payload));
            await adminStaffService.updateItemFull(item.itemId, formData);
            
            toast.success(item.isBestSeller ? "Đã gỡ khỏi Bán chạy!" : "Đã thêm vào Bán chạy!");
            updateItemInState(item.itemId, { isBestSeller: !item.isBestSeller });
        } catch (error) {
            toast.error("Lỗi khi thay đổi trạng thái bán chạy!");
        }
    };

    const handleUpdatePriority = async (id, val) => {
        try {
            updateItemInState(id, { priority: val });
            const res = await adminStaffService.updateItemPriority(id, val);
            if (res.status === 200) { toast.success("Đã chuyển vị trí hiển thị!"); }
        } catch (error) {
            console.error("Lỗi cập nhật priority:", error);
            toast.error(error.response?.data || "Lỗi khi lưu vị trí!");
            fetchData(); 
        }
    };

    const handleBoostToTop = (id) => {
        const newPriority = maxPriority + 10; 
        handleUpdatePriority(id, newPriority);
    };

    const handleSaveSystemTime = async () => {
        setSavingTime(true);
        try {
            const payload = {
                ...originalConfigs,
                flash_sale_start: systemFlashSaleTime.start,
                flash_sale_end: systemFlashSaleTime.end
            };
            await adminStaffService.updateSystemConfig(payload);
            toast.success("Đã cập nhật khung giờ Flash Sale chung!");
        } catch (e) {
            toast.error("Lỗi cập nhật cấu hình thời gian!");
        } finally {
            setSavingTime(false);
        }
    };

    const handleSaveRentalNote = async () => {
        setSavingNote(true);
        try {
            const payload = {
                ...originalConfigs,
                rental_note: rentalNote
            };
            await adminStaffService.updateSystemConfig(payload);
            setOriginalConfigs(payload); // Cập nhật lại config gốc
            toast.success("Đã cập nhật Ghi chú thuê sản phẩm!");
        } catch (e) {
            toast.error("Lỗi cập nhật ghi chú!");
        } finally {
            setSavingNote(false);
        }
    };

    const formatPrice = (price) => price ? new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price) : '';

    const filteredItems = items.filter(item => {
        const matchSearch = (item.itemName && item.itemName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                            (item.itemCode && item.itemCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
                            item.itemId.toString().includes(searchTerm);
        const matchCategory = selectedCategory === '' || (item.deviceCategory && item.deviceCategory.deviceCategoryId.toString() === selectedCategory);
        const matchPurpose = selectedPurpose === '' || (item.purposeIds && item.purposeIds.includes(parseInt(selectedPurpose)));
        const matchFlashSale = showOnlyFlashSale ? (item.itemFlashSalePrice && item.itemFlashSalePrice > 0) : true;
        const matchMainPage = showOnlyMainPage ? item.isShownInMainPage : true;
        // THÊM DÒNG NÀY:
        const matchBestSeller = showOnlyBestSeller ? item.isBestSeller : true; 
        const matchStatus = selectedStatus === '' || item.itemStatus.toString() === selectedStatus;

        // CẬP NHẬT DÒNG RETURN NÀY:
        return matchSearch && matchCategory && matchPurpose && matchFlashSale && matchMainPage && matchBestSeller && matchStatus;
    });

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
    
    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const getPaginationRange = () => {
        const delta = 2; 
        const range = [];
        for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
            range.push(i);
        }
        if (currentPage - delta > 2) range.unshift("...");
        if (currentPage + delta < totalPages - 1) range.push("...");
        range.unshift(1);
        if (totalPages > 1) range.push(totalPages);
        return range;
    };

    const StatusBadge = ({ status }) => {
        const config = PRODUCT_STATUS_CONFIG[status];
        if (!config) return <span className="bg-gray-100 text-gray-500 px-2 py-1 rounded text-xs font-bold">Lỗi</span>;
        const displayText = status === PRODUCT_STATUS.PRE_ORDER ? 'Đặt hàng' : config.text;
        return (
            <span className={`${config.bg} ${config.color} px-2 py-1 rounded text-xs font-bold flex items-center justify-center gap-1 w-max mx-auto`}>
                {/* <config.Icon size={12} /> Bỏ comment nếu muốn hiện cả Icon ở đây */}
                {displayText}
            </span>
        );
    };

    const openFlashSaleModal = (item) => {
        setEditingFlashSaleItem(item);
        setFlashSaleForm({
            price: item.itemFlashSalePrice || '',
            quantity: item.itemFlashSaleQuantity || ''
        });
    };

    const handleUpdateFlashSale = async (e) => {
        e.preventDefault();
        setUpdatingFlashSale(true);
        try {
            const retainedImageIds = editingFlashSaleItem.images ? editingFlashSaleItem.images.map(img => img.itemImageId) : [];
            const mainImage = editingFlashSaleItem.images?.find(img => img.isMain);
            const existingMainImageId = mainImage ? mainImage.itemImageId : null;

            // 👉 ĐÃ SỬA: Viết payload tường minh, đầy đủ y như hàm Best Seller để không bị mất dữ liệu nào
            const payload = {
                itemInfo: {
                    itemName: editingFlashSaleItem.itemName,
                    itemCode: editingFlashSaleItem.itemCode,
                    itemReleasePrice: editingFlashSaleItem.itemReleasePrice,
                    itemSalePrice: editingFlashSaleItem.itemSalePrice,
                    // Cập nhật giá và SL Flash Sale mới từ Form
                    itemFlashSalePrice: flashSaleForm.price ? Number(flashSaleForm.price) : null,
                    itemFlashSaleQuantity: flashSaleForm.quantity ? Number(flashSaleForm.quantity) : null,
                    itemQuantity: editingFlashSaleItem.itemQuantity,
                    itemStatus: editingFlashSaleItem.itemStatus,
                    categoryId: editingFlashSaleItem.categoryId || editingFlashSaleItem.deviceCategory?.deviceCategoryId,
                    brandId: editingFlashSaleItem.brandId || editingFlashSaleItem.brand?.brandId,
                    isShownInMainPage: editingFlashSaleItem.isShownInMainPage,
                    isBestSeller: editingFlashSaleItem.isBestSeller, 
                    description: editingFlashSaleItem.description,
                    warrantyTime: editingFlashSaleItem.warrantyTime,
                    priority: editingFlashSaleItem.priority || 0,
                    generalConfig: editingFlashSaleItem.generalConfig,
                    isForRent: editingFlashSaleItem.isForRent,
                    rentalPrices: editingFlashSaleItem.rentalPrices
                },
                purposes: editingFlashSaleItem.purposeIds || [],
                sales: editingFlashSaleItem.saleIds || [],
                itemSpecs: editingFlashSaleItem.itemSpecs ? editingFlashSaleItem.itemSpecs.map(spec => ({
                    specId: spec.specId, specName: spec.specName, specValue: spec.specValue
                })) : [],
                retainedImageIds: retainedImageIds,
                existingMainImageId: existingMainImageId,
                // 👉 ĐÃ THÊM: Không để bị xóa cấu hình PC Builder
                pcComponentId: editingFlashSaleItem.pcComponentId || null,
                compatibilityIds: editingFlashSaleItem.compatibilityIds || []
            };

            const formData = new FormData();
            formData.append("productData", JSON.stringify(payload));
            
            await adminStaffService.updateItemFull(editingFlashSaleItem.itemId, formData);
            
            toast.success("Đã thiết lập mức giá thành công!");
            
            updateItemInState(editingFlashSaleItem.itemId, {
                itemFlashSalePrice: flashSaleForm.price ? Number(flashSaleForm.price) : null,
                itemFlashSaleQuantity: flashSaleForm.quantity ? Number(flashSaleForm.quantity) : null
            });
            
            setEditingFlashSaleItem(null);
        } catch (error) { 
            toast.error("Lỗi cập nhật Flash Sale!"); 
        } 
        finally { 
            setUpdatingFlashSale(false); 
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <Monitor className="text-tnc-orange" size={24}/> Quản lý Sản phẩm
                </h2>
                <div className="flex items-center gap-3">
                    <button onClick={handleDownloadTemplate} className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md font-bold flex items-center gap-2 shadow-sm hover:bg-gray-50 transition-colors">
                        <DownloadCloud size={18} /> Tải Template
                    </button>

                    <label className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-bold flex items-center gap-2 shadow-sm transition-colors cursor-pointer">
                        <UploadCloud size={18} /> Import Excel
                        <input type="file" accept=".xlsx, .xls, .csv" onChange={handleFileUpload} ref={fileInputRef} className="hidden" />
                    </label>

                    <a 
                        href="/admin/products/create"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-tnc-orange hover:bg-tnc-orange-hover text-white px-4 py-2 rounded-md font-bold flex items-center gap-2 shadow-sm transition-colors"
                    >
                        <Plus size={18} /> Thêm Sản phẩm
                    </a>
                </div>
            </div>

            <div className="bg-gradient-to-r from-orange-50/50 to-orange-100/50 rounded-xl shadow-sm border border-tnc-orange overflow-hidden mb-6 p-4 md:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                    <h3 className="font-extrabold text-tnc-orange-hover text-lg flex items-center gap-2">
                        <Clock size={20} /> Cấu hình Đồng hồ Flash Sale Chung
                    </h3>
                    <p className="text-xs text-gray-600 mt-1">
                        Áp dụng chung cho tất cả các sản phẩm đang được bật giá Flash Sale bên dưới.
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                    <input 
                        type="datetime-local" 
                        value={systemFlashSaleTime.start}
                        onChange={(e) => setSystemFlashSaleTime({...systemFlashSaleTime, start: e.target.value})}
                        className="px-3 py-2 border border-gray-300 rounded outline-none focus:border-tnc-orange text-sm font-medium"
                        title="Thời gian Bắt đầu"
                    />
                    <span className="hidden sm:inline text-gray-400">➔</span>
                    <input 
                        type="datetime-local" 
                        value={systemFlashSaleTime.end}
                        onChange={(e) => setSystemFlashSaleTime({...systemFlashSaleTime, end: e.target.value})}
                        className="px-3 py-2 border border-gray-300 rounded outline-none focus:border-tnc-orange text-sm font-medium"
                        title="Thời gian Kết thúc"
                    />
                    <button 
                        onClick={handleSaveSystemTime}
                        disabled={savingTime}
                        className="bg-tnc-orange hover:bg-tnc-orange-hover text-white font-bold px-4 py-2 rounded shadow-sm transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
                    >
                        {savingTime ? 'Đang lưu...' : <><Save size={16}/> Cập nhật</>}
                    </button>
                </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50/50 to-blue-100/50 rounded-xl shadow-sm border border-blue-200 overflow-hidden mb-6 p-4 md:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="w-full md:w-1/3">
                    <h3 className="font-extrabold text-blue-700 text-lg flex items-center gap-2">
                        <FileText size={20} /> Cấu hình Ghi chú Cho Thuê
                    </h3>
                    <p className="text-xs text-gray-600 mt-1">
                        Hiển thị chung dưới bảng giá thuê ở trang chi tiết. Bạn có thể nhập Text bình thường hoặc thẻ HTML (như &lt;br&gt;, &lt;b&gt;...).
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch gap-3 w-full md:w-2/3">
                    <textarea 
                        value={rentalNote}
                        onChange={(e) => setRentalNote(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded outline-none focus:border-blue-500 text-sm font-medium min-h-[60px] resize-y"
                        placeholder="Nhập ghi chú cho thuê (ví dụ: * Yêu cầu đặt cọc CCCD...)"
                    />
                    <button 
                        onClick={handleSaveRentalNote}
                        disabled={savingNote}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded shadow-sm transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
                    >
                        {savingNote ? 'Đang lưu...' : <><Save size={16}/> Cập nhật</>}
                    </button>
                </div>
            </div>

            {/* BỘ LỌC & TÌM KIẾM */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
                <div className="p-5 border-b border-gray-100 bg-gray-50 flex flex-col gap-4">
                    {/* Hàng 1: Tìm kiếm & Tổng số */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="relative w-full md:max-w-md">
                            <input 
                                type="text" 
                                placeholder="Tìm theo tên, mã sản phẩm hoặc ID..." 
                                value={searchTerm} 
                                onChange={(e) => setSearchTerm(e.target.value)} 
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg outline-none focus:border-tnc-orange text-sm shadow-sm transition-colors"
                            />
                            <Search size={18} className="absolute left-3 top-3 text-gray-400" />
                        </div>
                        <div className="text-sm text-gray-500 font-medium whitespace-nowrap bg-white px-4 py-2.5 rounded-lg border border-gray-200 shadow-sm w-full md:w-auto text-center">
                            Tổng: <span className="text-tnc-orange font-bold text-base">{filteredItems.length}</span> SP
                        </div>
                    </div>

                    {/* Hàng 2: Các bộ lọc */}
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 text-gray-500 font-medium text-sm mr-2 hidden xl:flex">
                            <Filter size={18} /> Bộ lọc:
                        </div>
                        
                        <select 
                            value={selectedStatus} 
                            onChange={(e) => setSelectedStatus(e.target.value)} 
                            className="flex-1 min-w-[150px] border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-tnc-orange bg-white shadow-sm cursor-pointer transition-colors"
                        >
                            <option value="">Tất cả trạng thái</option>
                            {Object.entries(PRODUCT_STATUS_CONFIG).map(([key, config]) => (
                                <option key={key} value={key}>{config.text}</option>
                            ))}
                        </select>
                        
                        <select 
                            value={selectedCategory} 
                            onChange={(e) => setSelectedCategory(e.target.value)} 
                            className="flex-1 min-w-[150px] border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-tnc-orange bg-white shadow-sm cursor-pointer transition-colors"
                        >
                            <option value="">Tất cả phân loại</option>
                            {categories.map(cat => (<option key={cat.deviceCategoryId} value={cat.deviceCategoryId}>{cat.deviceCategoryName}</option>))}
                        </select>
                        
                        <select 
                            value={selectedPurpose} 
                            onChange={(e) => setSelectedPurpose(e.target.value)} 
                            className="flex-1 min-w-[150px] border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-tnc-orange bg-white shadow-sm cursor-pointer transition-colors"
                        >
                            <option value="">Tất cả danh mục</option>
                            {purposes.map(p => (<option key={p.purposeId} value={p.purposeId}>{p.purposeName}</option>))}
                        </select>
                        
                        <button 
                            onClick={() => setShowOnlyFlashSale(!showOnlyFlashSale)} 
                            className={`flex-1 min-w-[150px] flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-bold border rounded-lg transition-all shadow-sm ${showOnlyFlashSale ? 'bg-orange-50 text-tnc-orange border-tnc-orange' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-tnc-orange'}`}
                        >
                            <Zap size={16} className={showOnlyFlashSale ? "fill-current" : ""} /> {showOnlyFlashSale ? 'Đang lọc Flash Sale' : 'Lọc Flash Sale'}
                        </button>

                        {/* 4. THÊM NÚT LỌC TRANG CHỦ VÀO ĐÂY */}
                        <button 
                            onClick={() => setShowOnlyMainPage(!showOnlyMainPage)} 
                            className={`flex-1 min-w-[150px] flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-bold border rounded-lg transition-all shadow-sm ${showOnlyMainPage ? 'bg-green-50 text-green-600 border-green-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-green-600'}`}
                        >
                            <Monitor size={16} /> {showOnlyMainPage ? 'Đang lọc Trang chủ' : 'Lọc Trang chủ'}
                        </button>

                        <button 
                            onClick={() => setShowOnlyBestSeller(!showOnlyBestSeller)} 
                            className={`flex-1 min-w-[150px] flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-bold border rounded-lg transition-all shadow-sm ${showOnlyBestSeller ? 'bg-yellow-50 text-yellow-600 border-yellow-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-yellow-600'}`}
                        >
                            <Star size={16} className={showOnlyBestSeller ? "fill-current" : ""} /> {showOnlyBestSeller ? 'Đang lọc Bán chạy' : 'Lọc Bán chạy'}
                        </button>

                        {/* 5. CẬP NHẬT ĐIỀU KIỆN CỦA NÚT RESET */}
                        {(searchTerm || selectedCategory || selectedPurpose || showOnlyFlashSale || showOnlyMainPage || selectedStatus) && (
                            <button 
                                onClick={() => {
                                    setSearchTerm('');
                                    setSelectedCategory('');
                                    setSelectedPurpose('');
                                    setShowOnlyFlashSale(false);
                                    setShowOnlyMainPage(false); // Reset lại trạng thái
                                    setSelectedStatus('');
                                }} 
                                className="flex items-center justify-center p-2.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100 shrink-0" 
                                title="Xóa tất cả bộ lọc"
                            >
                                <X size={18} />
                            </button>
                        )}
                    </div>
                </div>

                <div className="overflow-x-auto min-h-[400px]">
                    <table className="w-full text-left border-collapse whitespace-nowrap">
                        <thead>
                            <tr className="bg-[#002855] text-white text-sm">
                                <th className="px-4 py-3 font-medium">Sản phẩm</th>
                                <th className="px-4 py-3 font-medium text-left">Phân loại & Danh mục</th>
                                <th className="px-4 py-3 font-medium text-center">Giới hạn đặt</th>
                                <th className="px-4 py-3 font-medium text-center">Bảng Giá (VNĐ)</th>
                                <th className="px-4 py-3 font-medium text-center">Trạng thái</th>
                                <th className="px-4 py-3 font-medium text-center" title="Số càng lớn càng xếp trên đầu trang chủ">
                                    <div className="flex items-center justify-center gap-1">
                                        <ArrowUpCircle size={16} className="text-tnc-orange" /> Ưu tiên
                                    </div>
                                </th>
                                <th className="px-4 py-3 font-medium text-center">Giá Flash Sale</th>
                                <th className="px-4 py-3 font-medium text-center">Thao tác</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {loading ? <tr><td colSpan="8" className="text-center py-16 text-gray-500 font-medium">Đang tải dữ liệu...</td></tr> : 
                            currentItems.length === 0 ? <tr><td colSpan="8" className="text-center py-16 text-gray-500 font-medium">Không tìm thấy sản phẩm nào!</td></tr> :
                            currentItems.map((item) => {
                                const isItemDisabled = item.itemStatus === PRODUCT_STATUS.HIDDEN || item.itemStatus === PRODUCT_STATUS.DISCONTINUED;

                                return (
                                    <tr key={item.itemId} className="border-b hover:bg-orange-50/30 transition-colors">
                                        <td className="px-4 py-3">
                                            <a 
                                                href={`/san-pham/${generateSlug(item.itemName)}-id${item.itemId}`}
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="font-bold text-gray-800 hover:text-tnc-orange line-clamp-3 max-w-[250px] whitespace-normal transition-colors cursor-pointer block" 
                                                title={`Xem chi tiết: ${item.itemName}`}
                                            >
                                                {item.itemName}
                                            </a>
                                            <div className="text-sm mt-1">
                                                {item.itemCode ? (
                                                    <span className="text-blue-600 font-mono">#{item.itemCode}</span>
                                                ) : (
                                                    <span className="text-red-500 italic text-xs font-sans font-medium">Chưa có mã</span>
                                                )}
                                                <span className="text-gray-400 mx-1.5">|</span>
                                                <span className="text-blue-600 font-mono">{item.brand?.brandName || 'N/A'}</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                                {/* 1. HIỂN THỊ SỐ LƯỢNG ẢNH (Code cũ của bạn) */}
                                                {item.images && item.images.length > 0 ? (
                                                    <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                                                        item.images.some(img => img.isMain)
                                                            ? 'bg-green-50 text-green-600 border border-green-200'
                                                            : 'bg-amber-50 text-amber-600 border border-amber-200'
                                                    }`}>
                                                        {item.images.length} ảnh {!item.images.some(img => img.isMain) && '⚠️'}
                                                    </span>
                                                ) : item.images === null ? (
                                                    <span className="text-[11px] text-gray-400 italic">N/A ảnh</span>
                                                ) : (
                                                    <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 text-red-500 border border-red-200">
                                                        Chưa có ảnh
                                                    </span>
                                                )}

                                                {/* 2. THÊM MỚI: HIỂN THỊ SỐ LƯỢNG THÔNG SỐ ĐÃ ĐIỀN */}
                                                {(() => {
                                                    // Lấy mảng specs từ API trả về (có thể là itemSpecs hoặc specs tùy BE)
                                                    const specs = item.itemSpecs || item.specs || [];
                                                    
                                                    // Lọc ra các spec thực sự có dữ liệu (tránh trường hợp mảng rỗng nhưng vẫn tạo object)
                                                    const validSpecsCount = specs.filter(s => s.specValue && String(s.specValue).trim() !== '').length;
                                                    
                                                    return validSpecsCount > 0 ? (
                                                        <span 
                                                            className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200 flex items-center gap-1"
                                                            title="Số lượng thông số kỹ thuật đã được điền"
                                                        >
                                                            <ClipboardList size={10} /> {validSpecsCount} thông số
                                                        </span>
                                                    ) : (
                                                        <span 
                                                            className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-gray-50 text-gray-500 border border-gray-200 flex items-center gap-1"
                                                            title="Chưa điền thông số kỹ thuật nào"
                                                        >
                                                            <ClipboardList size={10} /> 0 thông số
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-gray-800 font-bold flex items-center gap-1 mb-1.5">
                                                <Layers size={14} className="text-gray-400"/> {item.deviceCategory?.deviceCategoryName || '—'}
                                            </div>
                                            <div className="flex flex-col items-start gap-1">
                                                {item.purposeNames && item.purposeNames.length > 0 ? (
                                                    item.purposeNames.map((purpose, idx) => (
                                                        <span key={idx} className="text-[11px] text-tnc-orange font-bold bg-orange-50 px-2 py-0.5 rounded border border-orange-100 whitespace-nowrap">
                                                            {purpose}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-[11px] text-gray-400 font-medium italic bg-gray-50 px-2 py-0.5 rounded border border-gray-100">
                                                        Chưa gắn danh mục
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-center align-middle">
                                            <div className="font-bold text-gray-800 text-base"><span className="text-xs font-normal text-gray-400 block -mt-1">Giới hạn</span>{item.itemQuantity} </div>
                                            {(item.itemFlashSalePrice > 0 && item.itemFlashSaleQuantity > 0) && (
                                                <div className="text-[10px] text-tnc-orange font-bold mt-1.5 bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded flex items-center justify-center gap-1 mx-auto w-max shadow-sm" title={`Chỉ cho phép mua tối đa ${item.itemFlashSaleQuantity} SP`}>
                                                    <Zap size={10} fill="currentColor"/> GH: {item.itemFlashSaleQuantity}
                                                </div>
                                            )}
                                        </td>
                                        {/* Sửa thẻ td dưới đây: bỏ flex, thêm align-middle */}
                                        <td className="px-4 py-3 text-right align-middle">
                                            <div className="font-bold text-gray-800">{formatPrice(item.itemReleasePrice)}</div>
                                            {(item.itemSalePrice && item.itemSalePrice > 0) && (
                                                <div className="text-xs font-bold text-tnc-orange"><span className="text-gray-400 font-normal">Sale: </span>{formatPrice(item.itemSalePrice)}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <StatusBadge status={item.itemStatus} />
                                            <div className="mt-1.5 flex justify-center">

                                                {/* Code Nút Bán Chạy */}
                                                <button 
                                                    onClick={() => !isItemDisabled && handleToggleBestSeller(item)} 
                                                    disabled={isItemDisabled} 
                                                    className={`px-1.5 py-1 flex items-center rounded text-[10px] font-bold transition-all shadow-sm ${isItemDisabled ? 'bg-gray-100 text-gray-300 cursor-not-allowed opacity-50' : (item.isBestSeller ? 'bg-orange-100 text-tnc-orange border border-orange-200' : 'bg-gray-100 text-gray-500 border border-gray-200 hover:text-yellow-600')}`} 
                                                    title={item.isBestSeller ? "Gỡ khỏi danh sách bán chạy" : "Đánh dấu là Sản phẩm Bán Chạy"}
                                                >
                                                    {item.isBestSeller ? <><Star size={12} className="mr-1" fill="currentColor"/> BÁN CHẠY</> : <><StarOff size={12} className="mr-1"/> THƯỜNG</>}
                                                </button>
                                            </div>
                                        </td>
                                        
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-center gap-1">
                                                <div className="flex flex-col">
                                                    <button onClick={() => handleUpdatePriority(item.itemId, (item.priority || 0) + 1)} className="text-gray-400 hover:text-green-500 transition-colors" title="Tăng 1 bậc"><ChevronUp size={16}/></button>
                                                    <button onClick={() => handleUpdatePriority(item.itemId, (item.priority || 0) - 1)} className="text-gray-400 hover:text-red-500 transition-colors" title="Giảm 1 bậc"><ChevronDown size={16}/></button>
                                                </div>
                                                <input 
                                                    type="number"
                                                    defaultValue={item.priority || 0}
                                                    onBlur={(e) => {
                                                        const val = parseInt(e.target.value) || 0;
                                                        if (val !== (item.priority || 0)) handleUpdatePriority(item.itemId, val);
                                                    }}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                    className="w-12 h-8 px-1 text-center border rounded outline-none font-bold text-sm bg-gray-50 focus:bg-white focus:border-tnc-orange"
                                                    title="Sửa điểm và nhấn Enter hoặc click ra ngoài để lưu"
                                                />
                                                <button onClick={() => handleBoostToTop(item.itemId)} className="ml-1 p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded transition-colors shadow-sm" title="Đẩy lên đứng đầu danh sách"><ChevronsUp size={16}/></button>
                                            </div>
                                        </td>

                                        <td className="px-4 py-3 text-center">
                                            <button 
                                                onClick={() => openFlashSaleModal(item)}
                                                className={`px-3 py-2 rounded-lg text-xs font-bold transition-all border w-full flex items-center justify-center gap-1 ${
                                                    item.itemFlashSalePrice > 0 
                                                    ? 'bg-orange-50 text-tnc-orange border-orange-200 hover:bg-tnc-orange hover:text-white shadow-sm' 
                                                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-red-50 hover:text-red-500 hover:border-red-200'
                                                }`}
                                            >
                                                <Zap size={14} className={item.itemFlashSalePrice > 0 ? "fill-current" : ""} />
                                                {item.itemFlashSalePrice > 0 ? formatPrice(item.itemFlashSalePrice) : 'Thiết lập'}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 align-middle">
                                            <div className="flex justify-center items-center gap-2">
                                                {/* Giữ nguyên các button ở trong */}
                                                <button 
                                                    onClick={() => !isItemDisabled && handleToggleMainPage(item)} 
                                                    disabled={isItemDisabled} 
                                                    className={`px-2 py-1.5 flex items-center rounded text-xs font-bold transition-all shadow-sm ${isItemDisabled ? 'bg-gray-100 text-gray-300 cursor-not-allowed opacity-50' : (item.isShownInMainPage ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500 border border-gray-200 hover:text-green-600')}`} 
                                                    title={isItemDisabled ? "Trạng thái này không thể hiện trang chủ" : "Bật/tắt trang chủ"}
                                                >
                                                    {item.isShownInMainPage ? <Eye size={14}/> : <EyeOff size={14}/>}
                                                </button>
                                                <a
                                                    href={`/admin/products/edit/${item.itemId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-white hover:bg-blue-600 bg-blue-50 border border-blue-200 p-1.5 rounded transition-colors shadow-sm"
                                                    title="Sửa sản phẩm"
                                                >
                                                    <Edit size={16} />
                                                </a>
                                                <button onClick={() => handleDelete(item)} className="text-red-500 hover:text-white hover:bg-red-600 bg-red-50 border border-red-200 p-1.5 rounded transition-colors shadow-sm" title="Ngừng kinh doanh"><Trash2 size={16} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                )})}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="flex flex-col md:flex-row items-center justify-between p-4 border-t border-gray-100 bg-white gap-4">
                        <span className="text-sm text-gray-500 font-medium">
                            Hiển thị <strong className="text-gray-900">{indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredItems.length)}</strong> trên tổng số <strong className="text-gray-900">{filteredItems.length}</strong> sản phẩm
                        </span>
                        
                        <div className="flex flex-wrap items-center gap-4">
                            {/* Danh sách nút phân trang */}
                            <div className="flex gap-1">
                                <button onClick={() => handlePageChange(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors shadow-sm"><ChevronLeft size={18} /></button>
                                    {getPaginationRange().map((page, index) => {
                                        if (page === "...") {
                                            return <span key={`dots-${index}`} className="px-2 py-1 text-gray-400 font-bold">...</span>;
                                        }
                                        return (
                                            <button 
                                                key={index} 
                                                onClick={() => handlePageChange(page)} 
                                                className={`w-9 h-9 rounded-lg font-bold text-sm transition-colors border shadow-sm ${
                                                    currentPage === page 
                                                        ? 'bg-tnc-orange text-white border-tnc-orange' 
                                                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}

                                <button onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors shadow-sm"><ChevronRight size={18} /></button>
                            </div>

                            {/* Khối nhập số trang nhảy nhanh */}
                            <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
                                <span className="text-sm text-gray-500 font-medium">Đến trang:</span>
                                <input 
                                    key={currentPage} // Quan trọng: Giúp input tự update lại value khi page thay đổi từ nút bấm
                                    type="number"
                                    min={1}
                                    max={totalPages}
                                    defaultValue={currentPage}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            const page = parseInt(e.target.value);
                                            if (page >= 1 && page <= totalPages) {
                                                handlePageChange(page);
                                            } else {
                                                e.target.value = currentPage; // Trả về page hiện tại nếu nhập láo
                                            }
                                        }
                                    }}
                                    onBlur={(e) => {
                                        const page = parseInt(e.target.value);
                                        if (page >= 1 && page <= totalPages && page !== currentPage) {
                                            handlePageChange(page);
                                        } else {
                                            e.target.value = currentPage;
                                        }
                                    }}
                                    className="w-16 h-9 px-2 text-center border border-gray-300 rounded-lg outline-none text-sm font-bold focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange shadow-sm transition-shadow"
                                    title="Nhập số trang và nhấn Enter"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Các Modal giữ nguyên... */}
            {editingFlashSaleItem && (
                <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden relative">
                        <div className="bg-gradient-to-r from-tnc-orange-dark to-tnc-orange-light p-4 text-white flex justify-between items-center">
                            <h3 className="font-bold text-lg flex items-center gap-2"><Zap size={20} fill="currentColor" /> Giá Flash Sale</h3>
                            <button onClick={() => setEditingFlashSaleItem(null)} className="hover:bg-white/20 p-1.5 rounded-full transition-colors"><X size={20} /></button>
                        </div>
                        
                        <form onSubmit={handleUpdateFlashSale} className="p-6">
                            <p className="text-sm text-gray-600 mb-5 line-clamp-2">Sản phẩm: <strong className="text-gray-900">{editingFlashSaleItem.itemName}</strong></p>
                            
                            <div className="space-y-4 mb-6">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Mức giá sốc (VNĐ)</label>
                                    <input 
                                        type="text" 
                                        placeholder="Để trống để Hủy"
                                        value={flashSaleForm.price ? new Intl.NumberFormat('vi-VN').format(flashSaleForm.price) : ''}
                                        onChange={(e) => {
                                            const rawValue = e.target.value.replace(/\D/g, ''); 
                                            setFlashSaleForm({...flashSaleForm, price: rawValue});
                                        }}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:border-tnc-orange focus:ring-2 focus:ring-tnc-orange/20 font-bold text-tnc-orange text-lg placeholder-gray-300"
                                    />
                                    <p className="text-xs text-gray-400 mt-1 italic">*Giá gốc hiện tại: {formatPrice(editingFlashSaleItem.itemReleasePrice)}</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Giới hạn mua (Số lượng)</label>
                                    <input 
                                        type="text" 
                                        placeholder="Ví dụ: 10"
                                        value={flashSaleForm.quantity ? new Intl.NumberFormat('vi-VN').format(flashSaleForm.quantity) : ''}
                                        onChange={(e) => {
                                            const rawValue = e.target.value.replace(/\D/g, '');
                                            setFlashSaleForm({...flashSaleForm, quantity: rawValue});
                                        }}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none focus:border-tnc-orange font-bold text-gray-700"
                                    />
                                </div>
                            </div>
                            
                            <div className="flex gap-3 pt-4 border-t border-gray-100">
                                <button type="button" onClick={() => setFlashSaleForm({ price: '', quantity: '' })} className="px-4 py-2 bg-gray-100 text-gray-600 font-bold rounded-lg hover:bg-gray-200 transition-colors text-sm">Xóa trắng</button>
                                <button type="submit" disabled={updatingFlashSale || (flashSaleForm.price && !flashSaleForm.quantity)} className="flex-1 bg-tnc-orange text-white font-bold py-2 rounded-lg hover:bg-tnc-orange-hover transition-colors shadow-md disabled:opacity-50 flex justify-center items-center">
                                    {updatingFlashSale ? "Đang lưu..." : "Cập nhật giá"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {importState.showModal && (
                <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 animate-in fade-in">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="bg-[#002855] p-4 text-white flex justify-between items-center">
                            <h3 className="font-bold text-lg flex items-center gap-2"><UploadCloud size={20} /> Xác nhận Import Sản phẩm</h3>
                            <button onClick={() => setImportState({ showModal: false, validRows: [], invalidRows: [], isImporting: false, progress: 0 })} disabled={importState.isImporting} className="hover:bg-white/20 p-1.5 rounded-full transition-colors disabled:opacity-50"><X size={20} /></button>
                        </div>
                        
                        <div className="p-6 flex-1 overflow-y-auto bg-gray-50">
                            {importState.isImporting ? (
                                <div className="flex flex-col items-center justify-center py-10">
                                    <div className="w-16 h-16 border-4 border-gray-200 border-t-green-500 rounded-full animate-spin mb-4"></div>
                                    <p className="text-lg font-bold text-gray-700 mb-2">Đang xử lý nhập liệu...</p>
                                    <div className="w-full max-w-md bg-gray-200 rounded-full h-3 mb-2 overflow-hidden border border-gray-300">
                                        <div className="bg-green-500 h-3 rounded-full transition-all duration-300" style={{ width: `${importState.progress}%` }}></div>
                                    </div>
                                    <p className="text-sm font-bold text-green-600">{importState.progress}%</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div className="bg-white border border-green-200 rounded-xl overflow-hidden shadow-sm">
                                        <div className="bg-green-50 border-b border-green-200 px-4 py-3 flex items-center gap-2">
                                            <CheckCircle size={18} className="text-green-600"/> 
                                            <span className="font-bold text-green-800">Hợp lệ ({importState.validRows.length} SP)</span>
                                        </div>
                                        <div className="max-h-60 overflow-y-auto p-4 custom-scrollbar">
                                            {importState.validRows.length === 0 ? (
                                                <p className="text-sm text-gray-500 italic text-center py-2">Không có sản phẩm nào hợp lệ</p>
                                            ) : (
                                                <ul className="space-y-2">
                                                    {importState.validRows.map((row, idx) => (
                                                        <li key={idx} className="text-sm text-gray-700 bg-gray-50 p-2 rounded border border-gray-100 flex justify-between">
                                                            <span className="font-bold truncate mr-4">{row.itemName}</span>
                                                            <span className="text-xs text-gray-500 font-mono shrink-0">{row.itemCode}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>

                                    {importState.invalidRows.length > 0 && (
                                        <div className="bg-white border border-red-200 rounded-xl overflow-hidden shadow-sm">
                                            <div className="bg-red-50 border-b border-red-200 px-4 py-3 flex items-center gap-2">
                                                <X size={18} className="text-red-600"/> 
                                                <span className="font-bold text-red-800">Không hợp lệ ({importState.invalidRows.length} SP) - Sẽ bị bỏ qua</span>
                                            </div>
                                            <div className="max-h-60 overflow-y-auto p-4 custom-scrollbar">
                                                <ul className="space-y-3">
                                                    {importState.invalidRows.map((row, idx) => (
                                                        <li key={idx} className="text-sm bg-red-50/50 p-3 rounded border border-red-100">
                                                            <div className="font-bold text-gray-800 mb-1">Dòng {row.row}: <span className="text-red-600">{row.name}</span></div>
                                                            <ul className="list-disc pl-5 text-xs text-red-500 space-y-0.5 font-medium">
                                                                {row.errors.map((err, i) => <li key={i}>{err}</li>)}
                                                            </ul>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {!importState.isImporting && (
                            <div className="p-4 border-t border-gray-100 flex justify-end gap-3 bg-white">
                                <button onClick={() => setImportState({ showModal: false, validRows: [], invalidRows: [], isImporting: false, progress: 0 })} className="px-5 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-lg transition-colors border border-gray-200">Hủy bỏ</button>
                                <button 
                                    onClick={executeImport} 
                                    disabled={importState.validRows.length === 0}
                                    className="px-6 py-2.5 bg-tnc-orange text-white font-bold hover:bg-tnc-orange-hover rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    <UploadCloud size={18} /> Bắt đầu Import
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {deleteConfirmTarget && (
                <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4 animate-in fade-in">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative">
                        <div className="mb-5 border-b border-gray-100 pb-4">
                            <h3 className="text-xl font-bold text-gray-800">Tùy chọn vô hiệu hóa</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Cài đặt trạng thái cho sản phẩm: <br/>
                                <strong className="text-[#002855]">{deleteConfirmTarget.itemName}</strong>
                            </p>
                        </div>
                        
                        <div className="flex flex-col gap-3">
                            <button 
                                onClick={() => executeDelete(deleteConfirmTarget.itemId, PRODUCT_STATUS.DISCONTINUED)}
                                className="w-full text-left px-5 py-4 rounded-xl border-2 border-orange-200 bg-orange-50 hover:bg-orange-100 hover:border-orange-300 transition-all group"
                            >
                                <div className="font-bold text-orange-700 flex items-center gap-2">
                                    <span className="text-xl">🚫</span> Ngừng kinh doanh
                                </div>
                                <div className="text-xs text-orange-700/80 mt-1.5 leading-relaxed">
                                    Sản phẩm không hiện trên danh sách, nhưng khách vẫn xem được nếu có link trực tiếp. <strong>Rất tốt để giữ lại Traffic từ Google (SEO).</strong>
                                </div>
                            </button>
                            
                            <button 
                                onClick={() => executeDelete(deleteConfirmTarget.itemId, PRODUCT_STATUS.HIDDEN)}
                                className="w-full text-left px-5 py-4 rounded-xl border-2 border-red-200 bg-red-50 hover:bg-red-100 hover:border-red-300 transition-all group"
                            >
                                <div className="font-bold text-red-700 flex items-center gap-2">
                                    <span className="text-xl">👁️‍🗨️</span> Ẩn hoàn toàn
                                </div>
                                <div className="text-xs text-red-700/80 mt-1.5 leading-relaxed">
                                    Khóa mọi đường truy cập. Khách vào link cũ từ Google sẽ bị chặn và báo lỗi Không tìm thấy.
                                </div>
                            </button>
                        </div>
                        
                        <button onClick={() => setDeleteConfirmTarget(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 bg-gray-100 rounded-full p-1.5"><X size={20}/></button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminItemList;